import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import { createCheckin, type CheckinRow, type FreeResult } from '../../lib/apiClient';
import { isTailored, questionsFor } from '../../lib/dashboard/checkinQuestions';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Textarea } from '../ui/textarea';
import { useToast } from '../ui/toast';

/**
 * The dashboard's **Check-in** tab, copied from production.
 *
 * Two panels side by side: the form, and the history. The whole tab is gated on
 * having taken the Bottleneck assessment — production shows a single line
 * instead when you have not, because the second question is tailored to your
 * Bottleneck type and there is nothing to tailor it to.
 *
 * The questions come from that type: `TYPE_CHECKIN_QUESTIONS`, falling back to
 * `DEFAULT_QUESTIONS`. Production shows its "Questions tailored to your type"
 * line only when the type actually has an entry.
 */
export function CheckinTab({
  latestBottleneck,
  checkins,
  onSaved,
}: {
  latestBottleneck: FreeResult | null;
  checkins: CheckinRow[];
  /** Called with the saved row, so the history updates without a page load. */
  onSaved: (checkin: CheckinRow) => void;
}) {
  const { status, session } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const toast = useToast();

  const [energy, setEnergy] = useState(5);
  const [focus, setFocus] = useState(5);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const typeSlug = latestBottleneck?.typeSlug ?? null;
  const questions = questionsFor(typeSlug);

  async function save() {
    if (!token || saving) return;
    setSaving(true);
    setFailed(false);

    const saved = await createCheckin(token, { energyLevel: energy, focusLevel: focus, note });
    setSaving(false);

    if (!saved) {
      setFailed(true);
      return;
    }
    setNote('');
    onSaved(saved);
    // Production's confirmation. The history updating underneath is not one:
    // it looks the same whether the write landed or the list was already there.
    toast({ title: 'Check-in saved' });
  }

  if (!latestBottleneck) {
    return (
      <div className="space-y-8 pt-6" data-testid="dashboard-panel-checkin">
        <div className="py-16 text-center text-muted-foreground text-sm" data-testid="checkin-locked">
          Complete your first Bottleneck Assessment to unlock daily check-ins.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pt-6" data-testid="dashboard-panel-checkin">
      <div className="grid md:grid-cols-2 gap-8">
        <section className="p-6 rounded-xl border border-border bg-card space-y-6" data-testid="checkin-form">
          <div>
            <h2 className="text-xl font-semibold">Daily Check-in</h2>
            {isTailored(typeSlug) ? (
              <p className="text-xs text-muted-foreground mt-1" data-testid="checkin-tailored">
                Questions tailored to your type:{' '}
                <span className="text-primary font-medium">{latestBottleneck.primaryType}</span>
              </p>
            ) : null}
          </div>

          <div className="space-y-6">
            {questions.map((question, index) => {
              const value = index === 0 ? energy : focus;
              const setValue = index === 0 ? setEnergy : setFocus;
              return (
                <div key={question.id} className="space-y-3" data-testid={`checkin-${question.id}`}>
                  <div className="flex justify-between items-baseline">
                    <label className="text-sm font-medium" htmlFor={`checkin-slider-${question.id}`}>
                      {question.label}
                    </label>
                    <span className="text-2xl font-bold text-primary" data-testid={`checkin-${question.id}-value`}>
                      {value}
                    </span>
                  </div>
                  <Slider
                    id={`checkin-slider-${question.id}`}
                    min={1}
                    max={10}
                    step={1}
                    value={value}
                    onValueChange={setValue}
                    aria-label={question.label}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{question.low}</span>
                    <span>{question.high}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="checkin-note">
              Note (optional)
            </label>
            <Textarea
              id="checkin-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="What shaped today?"
              data-testid="checkin-note"
              className="bg-background resize-none min-h-[80px]"
            />
          </div>

          <Button className="w-full" onClick={() => void save()} disabled={saving} data-testid="checkin-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Save Check-in
          </Button>

          {failed ? (
            <p className="text-sm font-medium" data-testid="checkin-error" role="status">
              Error saving check-in
            </p>
          ) : null}
        </section>

        <section className="p-6 rounded-xl border border-border bg-card space-y-4" data-testid="checkin-history">
          <h2 className="text-xl font-semibold">Check-in History</h2>
          {checkins.length > 0 ? (
            <div className="space-y-3 overflow-y-auto max-h-[480px] pr-1">
              {checkins.map((checkin) => (
                <div
                  key={checkin.id}
                  className="p-4 rounded-lg border border-border bg-background space-y-2"
                  data-testid="checkin-entry"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {shortDate(checkin.createdAt)}
                    </span>
                    <div className="flex gap-3 text-xs">
                      <span>
                        Energy <span className="text-primary font-bold">{checkin.energyLevel}</span>
                      </span>
                      <span>
                        Focus <span className="text-primary font-bold">{checkin.focusLevel}</span>
                      </span>
                    </div>
                  </div>
                  {checkin.note ? (
                    <p className="text-xs text-muted-foreground leading-relaxed">{checkin.note}</p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="checkin-history-empty">
              No check-ins yet. Start tracking today.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

/** Production formats these with date-fns as `MMM d, yyyy`. */
function shortDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
